import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Button from '../../atoms/Button';
import Drawer from '../../atoms/Drawer';
import IconButton from '../../atoms/IconButton';
import Form from '../../molecules/Form';
import Checkbox from '../../atoms/Checkbox';
import { getInquiryToday, MOBILE_SUMMARY_FILTER_TYPE, ELECTRIC_SUMMARY_FILTER_TYPE, INTERNET_SUMMARY_FILTER_TYPE } from '../../../Common';
import { AppState } from '../../../redux/modules/store';
import ActDatepicker from '../../organisms/ActDatepicker';
import SummaryFilterTypeComp from '../../molecules/SummaryFilterType';
import { resetPaginationInfo, updateFilterSearchCondition } from '../../../redux/operations/LinkedDataResult';
import { FilterConditionLinkedDataState } from '../../../redux/reducers/FilterLinkedDataReducer';
import { getDurationFromValue, getDurationToValue, getErrorMessage, getTimeValue, isCheckedAllCheckbox } from './function';

interface FilterLinkedDataDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  storeSelectSection?: ReactNode;
  search: (request: FilterConditionLinkedDataState) => void;
}

const FilterLinkedDataDrawer: React.FC<FilterLinkedDataDrawerProps> = ({ isOpen, onClose, storeSelectSection, search }) => {
  const dispatch = useDispatch();
  const filterLinkedData = useSelector((state: AppState) => state.filterLinkedData);
  const fetchLoadingStatus = useSelector((state: AppState) => state.loadingStatus.filterLinkedData);

  const [timeValueFrom, setTimeValueFrom] = useState<string>('');
  const [timeValueTo, setTimeValueTo] = useState<string>('');
  const [listCheckedInternet, setListCheckedInternet] = useState<string[]>([]);
  const [listCheckedMobile, setListCheckedMobile] = useState<string[]>([]);
  const [listCheckedElectric, setListCheckedElectric] = useState<string[]>([]);
  const [error, setError] = useState<string[]>([]);

  const jobDateTime = fetchLoadingStatus.result?.jobDateTime;
  const inquiryToday = getInquiryToday(jobDateTime);

  // サマリー種類のチェックボックス状態を更新する共通関数 / function to render text "action complete date" column
  const handleUpdateListCheckedValue = (
    newListcheckedMobile: string[] = [],
    newListCheckedInternet: string[] = [],
    newListcheckedElectric: string[] = [],
  ) => {
    setListCheckedMobile(newListcheckedMobile);
    setListCheckedInternet(newListCheckedInternet);
    setListCheckedElectric(newListcheckedElectric);
  };

  // 「すべて」チェックボックス押下でデータを設定する関数 / function click checkbox 'すべて' and set data
  const onCheckedAllCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
    const ischecked: boolean = e.target.checked;
    let newListcheckedMobile: string[] = [];
    let newListCheckedInternet: string[] = [];
    let newListcheckedElectric: string[] = [];

    if (ischecked) {
      newListcheckedMobile = Object.keys(MOBILE_SUMMARY_FILTER_TYPE);
      newListCheckedInternet = Object.keys(INTERNET_SUMMARY_FILTER_TYPE);
      newListcheckedElectric = Object.keys(ELECTRIC_SUMMARY_FILTER_TYPE);
    }

    handleUpdateListCheckedValue(newListcheckedMobile, newListCheckedInternet, newListcheckedElectric);
  };

  // 「すべて」チェックボックスの状態を設定する関数 / function set status of checkbox 'すべて'
  const isCheckedAll = useMemo(
    () => isCheckedAllCheckbox(listCheckedMobile, listCheckedInternet, listCheckedElectric),
    [listCheckedElectric, listCheckedInternet, listCheckedMobile],
  );
  const durationValueFrom = useMemo(() => getDurationFromValue(timeValueFrom), [timeValueFrom]);
  const durationValueTo = useMemo(() => getDurationToValue(timeValueTo), [timeValueTo]);

  // datepickerフィールドを変更する共通関数 / function common changee fields datepicker
  const onchangeTime = (dt: Date | null, type = 'to') => {
    const time = getTimeValue(dt);
    if (type === 'to') {
      setTimeValueTo(time);
    } else {
      setTimeValueFrom(time);
    }
  };

  // ドロワーが開けた際にデータフィールドを設定する / set data field when drawer open
  const onOpenDrawer = () => {
    const { fromDate, toDate, mobile, internet, electric } = filterLinkedData;

    setTimeValueFrom(fromDate);
    setTimeValueTo(toDate);
    setError([]);
    handleUpdateListCheckedValue(mobile, internet, electric);
  };

  // 当時のデータ絞り込みで検索し、データをstoreに保持する / search with current data filter and save data in store
  const onSearch = () => {
    const errorMessage: string[] = getErrorMessage(timeValueFrom, timeValueTo);
    if (errorMessage.length) {
      setError(errorMessage);
      return;
    }
    const filterData = {
      fromDate: timeValueFrom,
      toDate: timeValueTo,
      mobile: listCheckedMobile,
      internet: listCheckedInternet,
      electric: listCheckedElectric,
    };
    search(filterData);
    updateFilterSearchCondition(filterData, dispatch);
    resetPaginationInfo(dispatch);
    onCloseDrawer();
  };

  const onClear = () => {
    setTimeValueFrom('');
    setTimeValueTo('');
    setError([]);
    handleUpdateListCheckedValue();
  };

  const onCloseDrawer = () => {
    onClose();
  };

  useEffect(() => {
    if (!isOpen) return;
    onOpenDrawer();
  }, [isOpen]);

  return (
    <>
      <Drawer className='filter-drawer' isOpen={isOpen} isOverayTransparent onClose={onCloseDrawer} position='right'>
        <div className='filter-drawer__inner'>
          <div className='filter-drawer__header'>
            <h3 className='headline-4 u-float-left'>絞り込み条件</h3>
            <IconButton
              className='u-float-right u-mn8'
              data-testid-e2e='filter-linked-data-drawer-close'
              data-testid='filter-linked-data-drawer-close'
              icon='close'
              onClick={onCloseDrawer}
            />
          </div>
          <hr className='uc-divider' />
          <div className='filter-drawer__content'>
            {error.length > 0 && (
              <div style={{ color: 'red', margin: '0.5em' }}>
                <span data-testid-e2e='filter-linked-data-drawer-error-title'>エラーが発生しました。下記の内容を確認してください。</span>
                <div data-testid-e2e='filter-linked-data-drawer-error-item'>
                  {error.map((errorName: string) => (
                    <li key={errorName}>{errorName}</li>
                  ))}
                </div>
              </div>
            )}
            <div className='uc-form-field u-mt24'>
              <div className='uc-form-field__label u-text-bold'>受付日</div>
              <div className='uc-form-field__control'>
                <Form className='filter-datefield_register'>
                  <Form.Fileld labelFor='templateSelect'>
                    <div className='u-flex u-align-center'>
                      <div data-testid-e2e='filter-linked-data-drawer-from-time' data-testid='filter-linked-data-drawer-from-time'>
                        <ActDatepicker
                          isClearable={true}
                          popperPlacement='bottom-start'
                          value={durationValueFrom}
                          defaultSelected={inquiryToday}
                          onChange={(dt) => onchangeTime(dt, 'from')}
                          onTodayButtonClick={() => onchangeTime(inquiryToday, 'from')}
                        />
                      </div>

                      <span className='uc-form-field__inner-text u-text-center' style={{ flex: 1 }}>
                        〜
                      </span>

                      <div data-testid-e2e='filter-linked-data-drawer-to-time' data-testid='filter-linked-data-drawer-to-time'>
                        <ActDatepicker
                          isClearable={true}
                          popperPlacement='bottom-start'
                          value={durationValueTo}
                          defaultSelected={inquiryToday}
                          onChange={(dt) => onchangeTime(dt)}
                          onTodayButtonClick={() => onchangeTime(inquiryToday)}
                        />
                      </div>
                    </div>
                  </Form.Fileld>
                </Form>
              </div>
            </div>
            <div
              className='uc-form-field u-mt24'
              data-testid='filter-linked-data-drawer-checkall-section'
              data-testid-e2e='filter-linked-data-drawer-checkall-section'>
              <div
                data-testid='filter-linked-data-drawer-checkall-title'
                data-testid-e2e='filter-linked-data-drawer-checkall-title'
                className='uc-form-field__label u-text-bold'>
                受付内容
              </div>
              <Checkbox
                data-testid='filter-linked-data-drawer-checkall'
                data-testid-e2e='filter-linked-data-drawer-checkall'
                className='u-ml'
                labelClassName='u-mv8 u-h-auto'
                label='すべて'
                name='example-checkbox'
                onChange={onCheckedAllCheckbox}
                checked={isCheckedAll}
              />
            </div>
            <SummaryFilterTypeComp
              listItem={MOBILE_SUMMARY_FILTER_TYPE}
              title='モバイル'
              listChecked={listCheckedMobile}
              setListChecked={setListCheckedMobile}
              type='mobile'
            />
            <SummaryFilterTypeComp
              listItem={INTERNET_SUMMARY_FILTER_TYPE}
              title='インターネット・固定電話'
              listChecked={listCheckedInternet}
              setListChecked={setListCheckedInternet}
              type='internet'
            />
            <SummaryFilterTypeComp
              listItem={ELECTRIC_SUMMARY_FILTER_TYPE}
              title='おうちでんき'
              listChecked={listCheckedElectric}
              setListChecked={setListCheckedElectric}
              type='homeElectricity'
            />
            {storeSelectSection}
          </div>
          <hr className='uc-divider' />
          <div className='filter-drawer__footer'>
            <Button onClick={onClear} className='u-float-left' type='transparent' label='クリア' />
            <Button
              className='u-mbn8'
              width='s'
              label='絞り込み'
              onClick={onSearch}
              data-testid-e2e='filterSettingDrawer-execute'
              data-testid='filterSettingDrawer-execute'
            />
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default FilterLinkedDataDrawer;
